import { Amplify, type ResourcesConfig } from 'aws-amplify';
import { fetchAuthSession } from 'aws-amplify/auth';
import { I18n } from 'aws-amplify/utils';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useEffect, useRef } from 'react';
import { frDict } from '../amplify-dict';
import { axiosInstance } from '../axiosClient';
import { type KorintAPIError, KorintRequestError } from '../errors';
import {
  FEDERATED_REQUEST_FLAG,
  readFederatedSession,
  recordFederatedSuccess,
  renewFederatedSession,
} from '../federated-session';
import { IMPERSONATED_REQUEST_FLAG } from '../impersonation';
import {
  type ErrorDictionary,
  type ForUserError,
  type I18nResolver,
  resolveKorintErrorMessage,
} from '../errors-translator';

I18n.putVocabularies({ fr: { ...frDict } });
I18n.setLanguage('fr');

export type KorintReactAxiosSetupConfig = {
  tenant?: string;
  baseUrl: string;
  postHogKey: string | undefined;
  authConfig?: ResourcesConfig['Auth'];
  /** Brokers log in through an external identity provider instead of Amplify. */
  federatedLogin?: { startUrl: string; portalUrl?: string };
  errorTranslatorConfig: {
    dict: ErrorDictionary;
    defaultErrorMessage: ForUserError;
    errorCodesToIgnore?: string[];
    i18n?: I18nResolver;
  };
};

export function useAxiosSetupWithConfig(clientOptions: KorintReactAxiosSetupConfig) {
  const amplifyConfiguredRef = useRef(false);

  axiosInstance.defaults.baseURL = clientOptions.baseUrl;
  axiosInstance.defaults.headers.common.Tenant = clientOptions.tenant;

  if (clientOptions.authConfig && !amplifyConfiguredRef.current) {
    Amplify.configure({ Auth: clientOptions.authConfig });
    amplifyConfiguredRef.current = true;
  }

  useEffect(() => {
    const productInterceptor = axiosInstance.interceptors.request.use(axiosConfig => {
      if (axiosConfig.headers.Tenant === undefined) {
        axiosConfig.headers.Tenant = clientOptions.tenant;
      }

      return axiosConfig;
    });

    return () => {
      axiosInstance.interceptors.request.eject(productInterceptor);
    };
  }, [clientOptions.tenant]);

  useEffect(() => {
    const errorInterceptor = axiosInstance.interceptors.response.use(
      response => {
        if (
          (response.config as { [FEDERATED_REQUEST_FLAG]?: boolean })[FEDERATED_REQUEST_FLAG] ===
          true
        ) {
          recordFederatedSuccess();
        }
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as
          | (InternalAxiosRequestConfig & {
              _retriedAfterRefresh?: boolean;
              [IMPERSONATED_REQUEST_FLAG]?: boolean;
              [FEDERATED_REQUEST_FLAG]?: boolean;
            })
          | undefined;

        // Safety net for long-lived sessions: a 401 usually means the access token
        // just expired. Force a token refresh and replay the request once before
        // surfacing the error. The _retriedAfterRefresh guard prevents an infinite loop.
        // MUST_BE_AUTHENTICATED is the gateway's 401 for a request that presented no
        // credentials at all, so there is nothing to refresh.
        const presentedNoCredentials =
          (error.response?.data as { code?: string } | undefined)?.code === 'MUST_BE_AUTHENTICATED';

        // Never refresh a request that went out on an impersonation token:
        // `forceRefresh` returns the *operator's* own Cognito token, so the replay
        // would silently re-issue an impersonated request as a staff one. Read off
        // the request, not off storage — a session that expired mid-flight has
        // already been evicted by the time this 401 arrives.
        const isImpersonating = originalRequest?.[IMPERSONATED_REQUEST_FLAG] === true;

        // A federated session has no Amplify token to refresh: replay on a token another tab
        // just obtained, or log in again through the provider (single sign-on, no screen).
        const federatedLogin = clientOptions.federatedLogin;
        if (
          error.response?.status === 401 &&
          originalRequest?.[FEDERATED_REQUEST_FLAG] === true &&
          federatedLogin &&
          !presentedNoCredentials
        ) {
          const sentToken = String(originalRequest.headers.Authorization ?? '').replace(
            /^Bearer /,
            '',
          );
          const current = readFederatedSession();
          if (current && current.token !== sentToken && !originalRequest._retriedAfterRefresh) {
            originalRequest._retriedAfterRefresh = true;
            originalRequest.headers.Authorization = `Bearer ${current.token}`;
            return axiosInstance.request(originalRequest);
          }
          renewFederatedSession({});
          // The page is navigating away; settling would flash an error for nothing.
          return new Promise<never>(() => {});
        }

        if (
          error.response?.status === 401 &&
          originalRequest &&
          !presentedNoCredentials &&
          !isImpersonating &&
          originalRequest[FEDERATED_REQUEST_FLAG] !== true &&
          !originalRequest._retriedAfterRefresh
        ) {
          originalRequest._retriedAfterRefresh = true;
          try {
            const session = await fetchAuthSession({ forceRefresh: true });
            const refreshedToken = session.tokens?.accessToken?.toString();
            if (refreshedToken) {
              originalRequest.headers.Authorization = `Bearer ${refreshedToken}`;
              return axiosInstance.request(originalRequest);
            }
          } catch {
            // Refresh failed (e.g. expired refresh token) — fall through to error
            // translation; the app's auth guard handles redirecting to sign-in.
          }
        }

        const errorData = error.response as unknown as {
          data: KorintAPIError['payload'];
          status: number;
        };

        const isKorintError = (e: unknown): e is KorintAPIError => {
          return (
            typeof e === 'object' && e !== null && 'status' in e && typeof e.status === 'number'
          );
        };

        if (!isKorintError(error)) {
          throw new KorintRequestError(
            clientOptions.errorTranslatorConfig.defaultErrorMessage,
            errorData,
          );
        }

        const errorMessage =
          resolveKorintErrorMessage(errorData.data, clientOptions.errorTranslatorConfig) ??
          clientOptions.errorTranslatorConfig.defaultErrorMessage;

        throw new KorintRequestError(errorMessage, errorData);
      },
    );

    return () => {
      axiosInstance.interceptors.response.eject(errorInterceptor);
    };
  }, [clientOptions.errorTranslatorConfig]);
}
